import { Component, OnInit, OnDestroy, Inject, Optional, Input } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LanguageService } from '../../shared/services/language.service';
import { ProjectTypesService } from '../../shared/services/project-types.service';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
  MatTooltipDefaultOptions,
  MatTooltipModule,
} from '@angular/material/tooltip';
import { InfoBarWithStatusService } from '../../shared/services/info-bar-with-status.service';
import { Profile } from 'src/app/models/Profile';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 500,
  hideDelay: 500,
  touchendHideDelay: 500,
};

@Component({
  standalone: false,
  selector: 'app-metodology-dialog',
  templateUrl: './metodology-dialog.component.html',
  styleUrls: ['./metodology-dialog.component.css'],
  providers: [{ provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults }],
})
export class MetodologyDialogComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();

  variables: any;

  //Temporary static items.
  items: any;

  local_data: any; //local data is the data that has been passed from the parent to this dialog and will be passed back to the parent.

  methodPredInvalid: boolean = false;
  otherNameInvalid: boolean = false;
  effortInvalid: boolean = false;
  maxHoursInvalid: boolean = false;
  preMacroTypesInvalid: boolean = false;
  otherMacroNameInvalid: boolean = false;
  methodNameInvalid: boolean = false;
  invalidInputSubTask: boolean = false;

  methodPredError: string = '';
  otherNameError: string = '';
  effortError: string = '';
  maxHoursError: string = '';
  preMacroTypesError: string = '';
  otherMacroNameError: string = '';
  methodNameError: string = '';
  errorMessageSubTask: string = '';

  isAction: boolean = false;
  isUsers: boolean = false;
  isSubTask: boolean = false;
  isProjectType: boolean = false;
  isConfigurationType: boolean = false;
  isMacroActivities: boolean = false;
  isMacroActivitiesType: boolean = false;
  showMacro: boolean = false;
  filteredMacro: any[] = [];

  formGroup1: any;

  effort = '';

  otherType = false; //Flag to check if the user will add a custom input or use a predefined type.
  otherTypeMacro = false;

  riskInputEffort: any;

  isActionType: boolean = false;

  list: any;
  listInDB: any;
  macrosList: any;

  isEdit = false;
  itemIndex: number = 0;
  profiles: Profile[] = [];
  centers: string[] = [];
  filteredProfiles: Profile[] = [];

  constructor(private projectTypesService: ProjectTypesService,
    public dialogRef: MatDialogRef<MetodologyDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private infoBar: InfoBarWithStatusService,
    private _formBuilder: FormBuilder,
    private languageService: LanguageService
  ) {
    this.local_data = { ...data };
    this.items = [
      { name: this.variables?.implementation },
      { name: 'Review' },
      { name: this.variables?.other }
    ];
  }

  ngOnInit(): void {
    this.languageService.translations$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vars: any) => {
        this.variables = vars;
      });

    // Initialize component data
    this.isEdit = this.local_data.isEdit;
    this.itemIndex = this.local_data.itemIndex;

    // Add default values to predefined list
    this.list = this.local_data.list;
    this.items = this.local_data.item;
    this.listInDB = this.local_data.listInDB;
    this.profiles = Array.isArray(this.local_data.profiles) ? this.local_data.profiles : [];

    this.centers = [...new Set(this.profiles.map(p => p.center).filter(c => !!c))] as string[];
    this.filteredProfiles = this.profiles;

    this.isMacroActivities = this.local_data.isMacroActivities !== undefined ? this.local_data.isMacroActivities : false;
    this.isMacroActivitiesType = this.local_data.isMacroActivitiesType !== undefined ? this.local_data.isMacroActivitiesType : false;

    if (this.local_data.effort) this.effort = this.local_data.effort;

    // Initialize form group once
    this.initializeFormGroup();

    // Setup value change listeners
    this.setupFormValueChangeListeners();
  }

  private initializeFormGroup(): void {
    if (this.isEdit) {
      this.formGroup1 = this._formBuilder.group({
        methodNameCtrl: [this.local_data.item.metodology, Validators.required],
        maxHoursCtrl: [this.items.maxHours || '', [Validators.min(1)]],
        profileCtrl: [this.items.profileId || ''],
        centerCtrl: [''],
      });

      const existingProfile = this.profiles.find(p => p.id === this.items.profileId);
      if (existingProfile?.center) {
        this.formGroup1.get('centerCtrl')?.setValue(existingProfile.center);
        this.filteredProfiles = this.profiles.filter(p => p.center === existingProfile.center);
      }
    } else if (this.isMacroActivities && this.local_data.metodology) {
      this.otherType = false;
      this.showMacro = true;
      this.filteredMacro = this.local_data.list[0].names;

      let nControl = '';
      let tControl = '';
      if (!this.filteredMacro.includes(this.local_data.item.metodology)) {
        nControl = this.variables?.other || 'Other';
        tControl = this.local_data.item.metodology;
        this.otherTypeMacro = true;
      } else {
        nControl = this.local_data.item.metodology;
      }

      if (this.local_data.type === 'edit') {
        this.formGroup1 = this._formBuilder.group({
          methodPredCtrl: [{ value: this.local_data.metodology, disabled: true }, Validators.required],
          effortCtrl: [this.local_data.item.complexity, [Validators.required, Validators.min(1), Validators.max(100)]],
          preMacroTypesCtrl: [nControl, Validators.required],
          otherMacroNameCtrl: [tControl, Validators.required],
        });
      } else {
        this.formGroup1 = this._formBuilder.group({
          methodPredCtrl: [{ value: this.local_data.metodology, disabled: true }, Validators.required],
          effortCtrl: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
          preMacroTypesCtrl: ['', Validators.required],
          otherMacroNameCtrl: ['', Validators.required],
        });
      }
    } else {
      // Default/non-macro form group
      this.formGroup1 = this._formBuilder.group({
        methodPredCtrl: ['', Validators.required],
        methodNameCtrl: ['', Validators.required],
        otherNameCtrl: ['', Validators.required],
        effortCtrl: ['', [Validators.required, Validators.min(1), Validators.max(100)]],
        maxHoursCtrl: ['', [Validators.min(1)]],
        preMacroTypesCtrl: ['', Validators.required],
        otherMacroNameCtrl: ['', Validators.required],
        profileCtrl: [''],
        centerCtrl: [''],
      });
    }
  }

  private setupFormValueChangeListeners(): void {
    if (this.isMacroActivities && !this.isMacroActivitiesType && !this.local_data.metodology) {
      // Macro activities without specific metodology
      this.formGroup1.get('methodPredCtrl')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          this.otherType = result === this.variables?.other;
          this.showMacro = true;

          this.local_data.list.forEach((macro: any) => {
            if (macro.type === result) {
              this.filteredMacro = macro.names;
            }
          });
        });

      this.formGroup1.get('preMacroTypesCtrl')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((result1: any) => {
          this.otherTypeMacro = result1 === this.variables?.other;
          const macroItem = this.searchMacroListByMacro(result1);
          if (macroItem) {
            this.formGroup1.get('effortCtrl').setValue(macroItem.value, { emitEvent: false });
          }
        });
    } else if (this.isMacroActivities && this.local_data.metodology) {
      // Macro activities with specific metodology
      this.formGroup1.get('preMacroTypesCtrl')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((result1: any) => {
          this.otherTypeMacro = result1 === this.variables?.other;
          const macroItem = this.searchMacroListByMacro(result1);
          if (macroItem) {
            this.formGroup1.get('effortCtrl').setValue(macroItem.value, { emitEvent: false });
          }
        });
    } else {
      // Non-macro activities
      this.formGroup1.get('methodPredCtrl')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((result: any) => {
          if (result === this.variables?.other) {
            this.otherType = true;
            this.formGroup1.get('fourthCtrl')?.setValue('', { emitEvent: false });
          } else {
            this.otherType = false;
          }
        });
    }
  }

  onMethodSelectionChange(event: any) {
    this.macrosList = this.searchListByMethod(event.value).macro
  }

  searchListByMethod(methodName: any) {
    return this.list.find((item: any) => item.method === methodName
    );
  }

  hasMethod(methodName: any) {
    return this.listInDB.some((item: any) => item.metodology === methodName
    );
  }

  searchMacroListByMacro(macroName: any) {
    return this.macrosList.find((item: any) => item.name === macroName
    );
  }

  cleanErrors() {
    this.methodPredInvalid = false;
    this.otherNameInvalid = false;
    this.effortInvalid = false;
    this.maxHoursInvalid = false;
    this.preMacroTypesInvalid = false;
    this.otherMacroNameInvalid = false;
    this.methodNameInvalid = false;
    this.invalidInputSubTask = false;

    this.methodPredError = '';
    this.otherNameError = '';
    this.effortError = '';
    this.maxHoursError = '';
    this.preMacroTypesError = '';
    this.otherMacroNameError = '';
    this.methodNameError = '';
    this.errorMessageSubTask = '';
  }


  doAction() {
    const formGroup = this.formGroup1.value;
    let valid = true;
    this.cleanErrors()

    if (!this.formGroup1.valid) {
      if (!formGroup.methodPredCtrl) {
        this.methodPredInvalid = true;
        this.methodPredError = this.variables.chooseProjectType;
        valid = false;
      }
    }

    if (this.isEdit) {
      if (this.hasMethod(formGroup.methodNameCtrl)) {
        this.methodNameInvalid = true;
        this.methodNameError = this.variables.snackBarErrorAddMetodology;
        valid = false;
      }
    }
    else {
      if (this.hasMethod(formGroup.methodPredCtrl) && formGroup.methodPredCtrl != this.variables.other && !this.otherType) {
        this.methodPredInvalid = true;
        this.methodPredError = this.variables.snackBarErrorAddMetodology;
        valid = false;
      }
      else if (formGroup.otherNameCtrl != '' && this.hasMethod(formGroup.otherNameCtrl) && this.otherType) {
        this.otherNameInvalid = true;
        this.otherNameError = this.variables.alreadyExists;
        valid = false;
      }

/*
      if (formGroup.effortCtrl === '' || (formGroup.effortCtrl <= 0 || formGroup.effortCtrl > 100)) {
        this.effortInvalid = true;
        this.effortError = this.variables.numberBetweenZeroUndred;
        valid = false;
      }

      if ((formGroup.otherNameCtrl === '' || formGroup.otherNameCtrl === undefined) && this.otherType) {
        this.otherNameInvalid = true;
        this.otherNameError = this.variables.insertName;
        valid = false;
      }

      if ((formGroup.preMacroTypesCtrl === '' || formGroup.preMacroTypesCtrl === undefined) && this.isMacroActivities && !this.isMacroActivitiesType) {
        this.preMacroTypesInvalid = true;
        this.preMacroTypesError = this.variables.selectMacroActivity;
        valid = false;
      }

      if ((formGroup.otherMacroNameCtrl === '' || formGroup.otherMacroNameCtrl === undefined) && this.otherTypeMacro) {
        this.otherMacroNameInvalid = true;
        this.otherMacroNameError = this.variables.insertName;
        valid = false;
      }

      if (formGroup.maxHoursCtrl !== '' && formGroup.maxHoursCtrl !== null && formGroup.maxHoursCtrl !== undefined && formGroup.maxHoursCtrl <= 0) {
        this.maxHoursInvalid = true;
        this.maxHoursError = this.variables.numberGreaterThenZero;
        valid = false;
      }

*/
    }

    this.local_data.effort = formGroup.effortCtrl;

    if (formGroup.methodNameCtrl) {
      this.local_data.name = formGroup.methodNameCtrl;
    }
    else if (formGroup.methodPredCtrl && formGroup.methodPredCtrl != this.variables.other) {
      this.local_data.name = formGroup.methodPredCtrl;
    }
    else if (formGroup.otherNameCtrl) {
      this.local_data.name = formGroup.otherNameCtrl;
    }

    this.local_data.maxHours = formGroup.maxHoursCtrl !== '' && formGroup.maxHoursCtrl !== null ? parseFloat(formGroup.maxHoursCtrl) : null;
    this.local_data.macroActivities = formGroup.preMacroTypesCtrl !== this.variables.other ? formGroup.preMacroTypesCtrl : formGroup.otherMacroNameCtrl
    this.local_data.profileId = formGroup.profileCtrl || null;
    this.local_data.centers = formGroup.centerCtrl || null;

    //Pass data
    if (valid) {
      this.dialogRef.close({ data: this.local_data });
    }
    else {
      this.infoBar.showSnackbar(this.variables.errorInForm, ['error-snackbar'], 2000);

    }
  }

  onCenterChange(center: string): void {
    if (center) {
      this.filteredProfiles = this.profiles.filter(p => p.center === center);
    } else {
      this.filteredProfiles = this.profiles;
    }
    this.formGroup1.get('profileCtrl')?.setValue('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
